import React, { useState, useEffect } from 'react';
import TopNavigation from './TopNavigation';
import './RequirementsForm_new.css';

const RequirementsForm = ({ onBack, onLogout }) => {
  const [isDragging, setIsDragging] = useState(false);
  
  const [projectInfo, setProjectInfo] = useState({
    projectName: '',
    version: '',
    date: new Date().toISOString().split('T')[0],
    author: '',
    description: '',
    
    // 검사 대상 정보
    inspectionTarget: '',
    targetSize: '',
    targetInfo: '',
    defectTypes: '',
    minDetectionSize: '',
    
    // 동작 조건
    operationState: '',
    movingSpeed: '',
    constantSpeed: '',
    
    // 설치 조건
    installationConstraints: '',
    
    // 환경 조건
    lightingConditions: '',
    temperatureRange: '',
    humidityRange: '',
    vibrationTolerance: '',
    dustEnvironment: '',
    
    // 카메라/광학
    requiredResolution: '',
    frameRate: '',
    lensSpecification: '',
    focalLength: '',
    apertureSetting: '',
    
    // 처리 성능
    processingTime: '',
    simultaneousTargets: '',
    memoryLimit: '',
    cpuLoadLimit: '',
    
    // 데이터 관리
    imageStoragePolicy: '',
    dataRetentionPeriod: '',
    backupPolicy: '',
    logFormat: '',
    
    // 통신/연동
    plcCommunication: '',
    systemIntegration: '',
    alarmOutput: '',
    remoteMonitoring: '',
    
    // 유지보수
    calibrationCycle: '',
    preventiveMaintenance: '',
    consumableReplacement: '',
    diagnosticFunction: ''
  });

  const handleProjectInfoChange = (field, value) => {
    setProjectInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const exportToJSON = () => {
    const data = {
      projectInfo,
      exportDate: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${projectInfo.projectName || 'project'}_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const printDocument = () => {
    window.print();
  };

  const resetForm = () => {
    if (window.confirm('모든 입력 내용을 초기화하시겠습니까?\n이 작업은 되돌릴 수 없습니다.')) {
      setProjectInfo({
        projectName: '',
        version: '',
        date: '',
        author: '',
        description: '',
        
        // 검사 대상 정보
        inspectionTarget: '',
        targetSize: '',
        targetInfo: '',
        defectTypes: '',
        minDetectionSize: '',
        
        // 동작 조건
        operationState: '',
        movingSpeed: '',
        constantSpeed: '',
        
        // 설치 조건
        installationConstraints: '',
        
        // 환경 조건
        operatingTemperature: '',
        operatingHumidity: '',
        lightingConditions: '',
        vibrationTolerance: '',
        dustEnvironment: '',
        
        // 카메라/광학
        requiredResolution: '',
        frameRate: '',
        lensSpecification: '',
        focalLength: '',
        apertureSetting: '',
        
        // 처리 성능
        processingTime: '',
        simultaneousTargets: '',
        accuracyRequirement: '',
        cpuLoadLimit: '',
        
        // 데이터 관리
        imageStoragePolicy: '',
        dataRetentionPeriod: '',
        backupPolicy: '',
        logFormat: '',
        
        // 통신/연동
        plcCommunication: '',
        systemIntegration: '',
        alarmOutput: '',
        remoteMonitoring: '',
        
        // 유지보수
        calibrationCycle: '',
        preventiveMaintenance: '',
        consumableReplacement: '',
        diagnosticFunction: ''
      });
    }
  };

  const importFromJSON = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // 파일 크기 체크 (10MB 제한)
    if (file.size > 10 * 1024 * 1024) {
      alert('파일 크기가 너무 큽니다. 10MB 이하의 파일을 선택해주세요.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        
        // 프로젝트 정보 불러오기
        if (data.projectInfo && typeof data.projectInfo === 'object') {
          setProjectInfo(prevInfo => ({
            ...prevInfo,
            ...data.projectInfo
          }));
          alert('프로젝트 정보를 성공적으로 불러왔습니다!');
        } else {
          alert('불러올 수 있는 유효한 프로젝트 정보가 없습니다.');
        }
        
      } catch (error) {
        alert('파일을 읽는 중 오류가 발생했습니다.\n올바른 JSON 파일인지 확인해주세요.\n\n오류 내용: ' + error.message);
        console.error('Import error:', error);
      }
    };
    
    reader.onerror = () => {
      alert('파일을 읽는 중 오류가 발생했습니다.');
    };
    
    reader.readAsText(file);
    // 파일 input 초기화 (같은 파일을 다시 선택할 수 있도록)
    event.target.value = '';
  };

  const handleImportClick = () => {
    document.getElementById('file-import').click();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type === 'application/json' || file.name.endsWith('.json')) {
        // 가상의 이벤트 객체 생성
        const fakeEvent = {
          target: {
            files: [file],
            value: ''
          }
        };
        importFromJSON(fakeEvent);
      } else {
        alert('JSON 파일만 업로드할 수 있습니다.');
      }
    }
  };

  return (
    <div 
      className={`requirements-form ${isDragging ? 'drag-over' : ''}`}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="fixed-header-section">
        <TopNavigation onBack={onBack} onLogout={onLogout} />

        {/* 메인 헤더 */}
        <header className="requirements-header">
          <div className="header-left">
            <h1>비전검사 요구사항</h1>
          </div>
          <div className="header-actions">
            <input
              id="file-import"
              type="file"
              accept=".json"
              onChange={importFromJSON}
              style={{ display: 'none' }}
            />
            <button onClick={handleImportClick} className="btn btn-secondary">
              📁 JSON 불러오기
            </button>
            <button onClick={exportToJSON} className="btn btn-primary">
              💾 JSON 내보내기
            </button>
            <button onClick={resetForm} className="btn btn-danger">
              🔄 초기화
            </button>
            <button onClick={printDocument} className="btn btn-secondary">
              🖨️ 인쇄
            </button>
          </div>
        </header>

        {/* 사용 가이드 */}
        <section className="usage-guide">
          <div className="guide-content">
            <p>
              📌 <strong>사용 방법:</strong> 
              프로젝트 정보를 입력하고 <strong>💾 JSON 내보내기</strong>로 저장하세요. 
              저장된 파일은 <strong>📁 JSON 불러오기</strong> 버튼이나 <strong>드래그 앤 드롭</strong>으로 다시 불러올 수 있습니다.
            </p>
          </div>
        </section>
      </div>

      {/* 스크롤 가능한 영역: 모든 입력 폼들 */}
      <div className="scrollable-content"
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >

      {/* 프로젝트 기본 정보 섹션 */}
      <section className="project-info-section">
        <h3 className="section-subtitle">프로젝트 정보</h3>
        <div className="form-grid-inline">
          <div className="form-group">
            <label htmlFor="projectName">프로젝트명</label>
            <input
              id="projectName"
              type="text"
              value={projectInfo.projectName}
              onChange={(e) => handleProjectInfoChange('projectName', e.target.value)}
              placeholder="프로젝트명을 입력하세요"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="version">버전</label>
            <input
              id="version"
              type="text"
              value={projectInfo.version}
              onChange={(e) => handleProjectInfoChange('version', e.target.value)}
              placeholder="예: v1.0.0"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="date">작성일</label>
            <input
              id="date"
              type="date"
              value={projectInfo.date}
              onChange={(e) => handleProjectInfoChange('date', e.target.value)}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="author">작성자</label>
            <input
              id="author"
              type="text"
              value={projectInfo.author}
              onChange={(e) => handleProjectInfoChange('author', e.target.value)}
              placeholder="작성자명을 입력하세요"
            />
          </div>
        </div>
        
        <div className="form-group">
          <label htmlFor="description">프로젝트 설명</label>
          <textarea
            id="description"
            value={projectInfo.description}
            onChange={(e) => handleProjectInfoChange('description', e.target.value)}
            placeholder="프로젝트에 대한 간단한 설명을 입력하세요"
            rows="3"
          />
        </div>
      </section>

      {/* 검사 대상 정보 섹션 */}
      <section className="project-info-section">
        {/* 검사 대상 정보 */}
        <h3 className="section-subtitle">🎯 검사 대상 정보</h3>
        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="inspectionTarget">검사 대상</label>
            <input
              id="inspectionTarget"
              type="text"
              value={projectInfo.inspectionTarget}
              onChange={(e) => handleProjectInfoChange('inspectionTarget', e.target.value)}
              placeholder="예: PCB, 반도체 웨이퍼, 자동차 부품"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="targetSize">검사 대상 크기</label>
            <input
              id="targetSize"
              type="text"
              value={projectInfo.targetSize}
              onChange={(e) => handleProjectInfoChange('targetSize', e.target.value)}
              placeholder="예: 100mm x 200mm x 5mm"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="targetInfo">검사 대상 정보</label>
            <input
              id="targetInfo"
              type="text"
              value={projectInfo.targetInfo}
              onChange={(e) => handleProjectInfoChange('targetInfo', e.target.value)}
              placeholder="재질, 색상, 표면 특성 등"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="defectTypes">검출 목록</label>
            <input
              id="defectTypes"
              type="text"
              value={projectInfo.defectTypes}
              onChange={(e) => handleProjectInfoChange('defectTypes', e.target.value)}
              placeholder="예: 크랙, 스크래치, 이물질, 변색"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="minDetectionSize">최소 검출 크기 (μm)</label>
            <input
              id="minDetectionSize"
              type="text"
              value={projectInfo.minDetectionSize}
              onChange={(e) => handleProjectInfoChange('minDetectionSize', e.target.value)}
              placeholder="예: 10μm"
            />
          </div>
        </div>
      </section>

      {/* 동작 조건 섹션 */}
      <section className="project-info-section">
        {/* 동작 조건 */}
        <h3 className="section-subtitle">⚙️ 동작 조건</h3>
        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="operationState">정지/이동 상태</label>
            <select
              id="operationState"
              value={projectInfo.operationState}
              onChange={(e) => handleProjectInfoChange('operationState', e.target.value)}
            >
              <option value="">선택하세요</option>
              <option value="정지">정지 상태</option>
              <option value="이동">이동 상태</option>
              <option value="정지+이동">정지 및 이동 모두</option>
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="movingSpeed">이동 시 속도</label>
            <input
              id="movingSpeed"
              type="text"
              value={projectInfo.movingSpeed}
              onChange={(e) => handleProjectInfoChange('movingSpeed', e.target.value)}
              placeholder="예: 100mm/s"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="constantSpeed">이동 시 등속 여부</label>
            <select
              id="constantSpeed"
              value={projectInfo.constantSpeed}
              onChange={(e) => handleProjectInfoChange('constantSpeed', e.target.value)}
            >
              <option value="">선택하세요</option>
              <option value="등속">등속</option>
              <option value="가변속">가변속</option>
              <option value="가감속포함">가감속 포함</option>
            </select>
          </div>
        </div>
      </section>

      {/* 설치 조건 섹션 */}
      <section className="project-info-section">
        {/* 설치 조건 */}
        <h3 className="section-subtitle">🏗️ 설치 조건</h3>
        <div className="form-group">
          <label htmlFor="installationConstraints">설치 제약조건</label>
          <textarea
            id="installationConstraints"
            value={projectInfo.installationConstraints}
            onChange={(e) => handleProjectInfoChange('installationConstraints', e.target.value)}
            placeholder="공간 제약, 접근성, 안전 요구사항 등"
            rows="2"
          />
        </div>
      </section>

      {/* 환경 조건 섹션 */}
      <section className="project-info-section">
        {/* 환경 조건 */}
        <h3 className="section-subtitle">🌡️ 환경 조건</h3>
        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="lightingConditions">조명 조건</label>
            <input
              id="lightingConditions"
              type="text"
              value={projectInfo.lightingConditions}
              onChange={(e) => handleProjectInfoChange('lightingConditions', e.target.value)}
              placeholder="예: LED 백라이트, 1000lux"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="temperatureRange">온도 범위</label>
            <input
              id="temperatureRange"
              type="text"
              value={projectInfo.temperatureRange}
              onChange={(e) => handleProjectInfoChange('temperatureRange', e.target.value)}
              placeholder="예: 15°C ~ 35°C"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="humidityRange">습도 범위</label>
            <input
              id="humidityRange"
              type="text"
              value={projectInfo.humidityRange}
              onChange={(e) => handleProjectInfoChange('humidityRange', e.target.value)}
              placeholder="예: 40% ~ 70% RH"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="vibrationTolerance">진동/충격 내성</label>
            <input
              id="vibrationTolerance"
              type="text"
              value={projectInfo.vibrationTolerance}
              onChange={(e) => handleProjectInfoChange('vibrationTolerance', e.target.value)}
              placeholder="예: 0.5G 이하"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="dustEnvironment">먼지/오염 환경</label>
            <input
              id="dustEnvironment"
              type="text"
              value={projectInfo.dustEnvironment}
              onChange={(e) => handleProjectInfoChange('dustEnvironment', e.target.value)}
              placeholder="예: IP65, 클린룸 Class 1000"
            />
          </div>
        </div>
      </section>

      {/* 카메라/광학 섹션 */}
      <section className="project-info-section">
        {/* 카메라/광학 */}
        <h3 className="section-subtitle">📷 카메라/광학 사양</h3>
        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="requiredResolution">해상도 요구사항</label>
            <input
              id="requiredResolution"
              type="text"
              value={projectInfo.requiredResolution}
              onChange={(e) => handleProjectInfoChange('requiredResolution', e.target.value)}
              placeholder="예: 2048x2048, 5MP"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="frameRate">프레임 레이트</label>
            <input
              id="frameRate"
              type="text"
              value={projectInfo.frameRate}
              onChange={(e) => handleProjectInfoChange('frameRate', e.target.value)}
              placeholder="예: 30fps"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="lensSpecification">렌즈 배율/화각</label>
            <input
              id="lensSpecification"
              type="text"
              value={projectInfo.lensSpecification}
              onChange={(e) => handleProjectInfoChange('lensSpecification', e.target.value)}
              placeholder="예: 1:1 매크로, 50mm"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="focalLength">초점 거리</label>
            <input
              id="focalLength"
              type="text"
              value={projectInfo.focalLength}
              onChange={(e) => handleProjectInfoChange('focalLength', e.target.value)}
              placeholder="예: 100mm"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="apertureSetting">조리개 설정</label>
            <input
              id="apertureSetting"
              type="text"
              value={projectInfo.apertureSetting}
              onChange={(e) => handleProjectInfoChange('apertureSetting', e.target.value)}
              placeholder="예: F/8"
            />
          </div>
        </div>
      </section>

      {/* 처리 성능 섹션 */}
      <section className="project-info-section">
        {/* 처리 성능 */}
        <h3 className="section-subtitle">⚡ 처리 성능</h3>
        <div className="form-grid-four">
          <div className="form-group">
            <label htmlFor="processingTime">처리 시간 요구사항</label>
            <input
              id="processingTime"
              type="text"
              value={projectInfo.processingTime}
              onChange={(e) => handleProjectInfoChange('processingTime', e.target.value)}
              placeholder="예: 100ms 이하"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="simultaneousTargets">동시 검사 대상 수</label>
            <input
              id="simultaneousTargets"
              type="text"
              value={projectInfo.simultaneousTargets}
              onChange={(e) => handleProjectInfoChange('simultaneousTargets', e.target.value)}
              placeholder="예: 최대 4개"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="memoryLimit">메모리 사용량 제한</label>
            <input
              id="memoryLimit"
              type="text"
              value={projectInfo.memoryLimit}
              onChange={(e) => handleProjectInfoChange('memoryLimit', e.target.value)}
              placeholder="예: 8GB 이하"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="cpuLoadLimit">CPU 부하 제한</label>
            <input
              id="cpuLoadLimit"
              type="text"
              value={projectInfo.cpuLoadLimit}
              onChange={(e) => handleProjectInfoChange('cpuLoadLimit', e.target.value)}
              placeholder="예: 80% 이하"
            />
          </div>
        </div>
      </section>

      {/* 데이터 관리 섹션 */}
      <section className="project-info-section">
        {/* 데이터 관리 */}
        <h3 className="section-subtitle">💾 데이터 관리</h3>
        <div className="form-grid-four">
          <div className="form-group">
            <label htmlFor="imageStoragePolicy">이미지 저장 정책</label>
            <select
              id="imageStoragePolicy"
              value={projectInfo.imageStoragePolicy}
              onChange={(e) => handleProjectInfoChange('imageStoragePolicy', e.target.value)}
            >
              <option value="">선택하세요</option>
              <option value="NG품만">NG품만 저장</option>
              <option value="모든이미지">모든 이미지 저장</option>
              <option value="샘플링">샘플링 저장</option>
              <option value="저장안함">저장하지 않음</option>
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="dataRetentionPeriod">데이터 보관 기간</label>
            <input
              id="dataRetentionPeriod"
              type="text"
              value={projectInfo.dataRetentionPeriod}
              onChange={(e) => handleProjectInfoChange('dataRetentionPeriod', e.target.value)}
              placeholder="예: 30일, 6개월"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="backupPolicy">백업 정책</label>
            <input
              id="backupPolicy"
              type="text"
              value={projectInfo.backupPolicy}
              onChange={(e) => handleProjectInfoChange('backupPolicy', e.target.value)}
              placeholder="예: 일일 자동 백업"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="logFormat">검사 결과 로그 형식</label>
            <select
              id="logFormat"
              value={projectInfo.logFormat}
              onChange={(e) => handleProjectInfoChange('logFormat', e.target.value)}
            >
              <option value="">선택하세요</option>
              <option value="CSV">CSV</option>
              <option value="JSON">JSON</option>
              <option value="XML">XML</option>
              <option value="Database">Database</option>
            </select>
          </div>
        </div>
      </section>

      {/* 통신/연동 섹션 */}
      <section className="project-info-section">
        {/* 통신/연동 */}
        <h3 className="section-subtitle">🔗 통신/연동</h3>
        <div className="form-grid-four">
          <div className="form-group">
            <label htmlFor="plcCommunication">PLC 통신 방식</label>
            <input
              id="plcCommunication"
              type="text"
              value={projectInfo.plcCommunication}
              onChange={(e) => handleProjectInfoChange('plcCommunication', e.target.value)}
              placeholder="예: Ethernet/IP, Modbus TCP"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="systemIntegration">상위 시스템 연동</label>
            <input
              id="systemIntegration"
              type="text"
              value={projectInfo.systemIntegration}
              onChange={(e) => handleProjectInfoChange('systemIntegration', e.target.value)}
              placeholder="예: MES, ERP 연동"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="alarmOutput">알람/경고 출력</label>
            <input
              id="alarmOutput"
              type="text"
              value={projectInfo.alarmOutput}
              onChange={(e) => handleProjectInfoChange('alarmOutput', e.target.value)}
              placeholder="예: 경광등, 부저, 이메일"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="remoteMonitoring">원격 모니터링</label>
            <input
              id="remoteMonitoring"
              type="text"
              value={projectInfo.remoteMonitoring}
              onChange={(e) => handleProjectInfoChange('remoteMonitoring', e.target.value)}
              placeholder="예: 웹 모니터링, VNC"
            />
          </div>
        </div>
      </section>

      {/* 유지보수 섹션 */}
      <section className="project-info-section">
        {/* 유지보수 */}
        <h3 className="section-subtitle">🔧 유지보수</h3>
        <div className="form-grid-four">
          <div className="form-group">
            <label htmlFor="calibrationCycle">교정/캘리브레이션 주기</label>
            <input
              id="calibrationCycle"
              type="text"
              value={projectInfo.calibrationCycle}
              onChange={(e) => handleProjectInfoChange('calibrationCycle', e.target.value)}
              placeholder="예: 월 1회, 분기 1회"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="preventiveMaintenance">예방보전 항목</label>
            <input
              id="preventiveMaintenance"
              type="text"
              value={projectInfo.preventiveMaintenance}
              onChange={(e) => handleProjectInfoChange('preventiveMaintenance', e.target.value)}
              placeholder="예: 렌즈 청소, 조명 점검"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="consumableReplacement">소모품 교체 주기</label>
            <input
              id="consumableReplacement"
              type="text"
              value={projectInfo.consumableReplacement}
              onChange={(e) => handleProjectInfoChange('consumableReplacement', e.target.value)}
              placeholder="예: LED 조명 2년, 필터 6개월"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="diagnosticFunction">진단 기능</label>
            <input
              id="diagnosticFunction"
              type="text"
              value={projectInfo.diagnosticFunction}
              onChange={(e) => handleProjectInfoChange('diagnosticFunction', e.target.value)}
              placeholder="예: 자동 진단, 상태 모니터링"
            />
          </div>
        </div>
      </section>
      </div> {/* scrollable-content 닫는 태그 */}
    </div>
  );
};

export default RequirementsForm;